import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PublicHeader from '../components/PublicHeader';
import {
  BuildingOfficeIcon,
  DocumentTextIcon,
  GlobeEuropeAfricaIcon,
  KeyIcon,
  ShieldCheckIcon,
  UsersIcon
} from '@heroicons/react/24/outline';

const FeatureCard = ({ icon: Icon, title, description }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <div className="flex items-center mb-4">
      <div className="rounded-md p-3 bg-primary-100">
        <Icon className="h-6 w-6 text-primary-600" />
      </div>
      <h3 className="ml-4 text-lg font-medium text-gray-900">{title}</h3>
    </div>
    <p className="text-gray-600">{description}</p>
  </div>
);

const TestimonialCard = ({ quote, author, role }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <div className="flex flex-col h-full">
      <div className="mb-4">
        <svg className="h-8 w-8 text-primary-400" fill="currentColor" viewBox="0 0 32 32">
          <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
        </svg>
      </div>
      <p className="flex-grow text-gray-600 mb-4">{quote}</p>
      <div>
        <p className="font-medium text-gray-900">{author}</p>
        <p className="text-sm text-gray-500">{role}</p>
      </div>
    </div>
  </div>
);

const PricingCard = ({ tier, price, features, cta, popular }) => (
  <div className={`bg-white rounded-lg shadow ${popular ? 'ring-2 ring-primary-600' : ''} relative`}>
    {popular && (
      <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2">
        <span className="inline-flex rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-600">
          Popular
        </span>
      </div>
    )}
    <div className="p-6">
      <h3 className="text-lg font-semibold text-gray-900">{tier}</h3>
      <p className="mt-2">
        <span className="text-3xl font-bold text-gray-900">${price}</span>
        <span className="text-base text-gray-500">/month</span>
      </p>
      <ul className="mt-6 space-y-4">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" clipRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
              </svg>
            </div>
            <p className="ml-3 text-gray-600">{feature}</p>
          </li>
        ))}
      </ul>
      <div className="mt-8">
        <Link to="/register" className={`block w-full py-3 px-4 rounded-md text-center font-medium ${popular ? 'bg-primary-600 text-white hover:bg-primary-700' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}>
          {cta}
        </Link>
      </div>
    </div>
  </div>
);

const LandingPage = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div className="bg-gray-50">
      {/* Header */}
      <PublicHeader />
      
      {/* Hero Section */}
      <div className="relative isolate overflow-hidden">
        <div className="absolute inset-x-0 top-0 z-10 h-16 bg-white/90"></div>
        
        <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
              The easiest way to generate professional rent receipts
            </h1>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Manage properties, tenants, and generate rent receipts all in one place. Save time and stay organized with our simple yet powerful platform.
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  className="rounded-md bg-primary-600 px-6 py-3 text-lg font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                >
                  Go to Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/register"
                    className="rounded-md bg-primary-600 px-6 py-3 text-lg font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                  >
                    Get Started
                  </Link>
                  <Link
                    to="/login"
                    className="text-lg font-semibold leading-6 text-gray-900"
                  >
                    Log in <span aria-hidden="true">→</span>
                  </Link>
                </>
              )}
            </div>
            <div className="mt-6">
              <Link to="/blog" className="text-sm font-semibold text-primary-600 hover:text-primary-500">
                Read our blog <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* French Market Announcement */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 py-8">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <div className="inline-flex items-center rounded-full bg-white/10 px-4 py-1.5 text-sm font-semibold text-white mb-4">
              <span className="mr-2">🇫🇷</span>
              New Features
            </div>
            <h2 className="text-2xl font-bold text-white sm:text-3xl mb-4">
              Full Support for French Rental Market
            </h2>
            <div className="space-y-3">
              <p className="text-lg text-blue-100">
                <strong>Rental Contracts:</strong> Create rental contracts for unfurnished apartments (non-meublé), fully compliant with French rental law.
              </p>
              <p className="text-lg text-blue-100">
                <strong>État des Lieux:</strong> Generate professional property condition reports (états des lieux d'entrée et de sortie) for rental properties in France.
              </p>
              <p className="text-lg text-blue-100 italic mt-4">
                RentReceipt prend désormais en charge la création de contrats de location pour appartements non meublés et les états des lieux pour les locations en France, entièrement conforme à la législation française.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-16 sm:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Features</h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Everything you need to manage rent receipts efficiently
            </p>
          </div>
          <div className="mx-auto mt-12 grid max-w-2xl grid-cols-1 gap-6 sm:mt-16 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3">
            <FeatureCard
              icon={BuildingOfficeIcon}
              title="Property Management"
              description="Keep track of all your rental properties in one place with detailed information."
            />
            <FeatureCard
              icon={UsersIcon}
              title="Tenant Tracking"
              description="Manage tenant information, lease details, and payment history."
            />
            <FeatureCard
              icon={DocumentTextIcon}
              title="Professional Receipts"
              description="Generate professional-looking PDF rent receipts with just a few clicks."
            />
            <FeatureCard
              icon={GlobeEuropeAfricaIcon}
              title="Multilingual Support"
              description="Generate receipts in multiple languages including English and French."
            />
            <FeatureCard
              icon={ShieldCheckIcon}
              title="Secure & Private"
              description="Your data is encrypted and secured. Only you have access to your information."
            />
            <FeatureCard
              icon={KeyIcon}
              title="User Management"
              description="Multiple users can access their own properties and manage their own tenants."
            />
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">What our users say</h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Don't just take our word for it - hear from some of our users
            </p>
          </div>
          <div className="mx-auto mt-12 grid max-w-2xl grid-cols-1 gap-6 sm:mt-16 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3">
            <TestimonialCard
              quote="This app has saved me so much time each month. I used to create receipts manually, but now I can generate them with just a few clicks."
              author="Sophie M."
              role="Property Manager"
            />
            <TestimonialCard
              quote="I love how I can generate receipts in both English and French. This is perfect for my properties in Montreal where I have both English and French-speaking tenants."
              author="Jean-Pierre L."
              role="Landlord"
            />
            <TestimonialCard
              quote="The organization of properties and tenants makes it so easy to keep track of everything. I can't imagine going back to spreadsheets."
              author="Alex K."
              role="Real Estate Investor"
            />
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="py-16 sm:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Pricing</h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Choose the plan that works best for you
            </p>
          </div>
          <div className="mx-auto mt-12 grid max-w-md grid-cols-1 gap-8 lg:max-w-4xl lg:grid-cols-2">
            <PricingCard
              tier="Basic"
              price="0"
              features={[
                "Up to 3 properties",
                "Up to 10 tenants",
                "English receipts only",
                "Email support"
              ]}
              cta="Sign up for free"
            />
            <PricingCard
              tier="Pro"
              price="19.99"
              popular={true}
              features={[
                "Unlimited properties",
                "Unlimited tenants",
                "Multilingual receipts",
                "Priority support",
                "Receipt customization",
                "Email sending"
              ]}
              cta="Start your free trial"
            />
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Ready to get started?</h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Join thousands of property owners who are already saving time with our platform
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <Link
                to="/register"
                className="rounded-md bg-primary-600 px-6 py-3 text-lg font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
              >
                Sign up now
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;